"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var feature_flipper_1 = require("@shared/constants/feature-flipper");
var environment_1 = require("@shared/types/environment");
var angular_1 = require("angular");
var lodash_1 = __importDefault(require("lodash"));
/**
 * Service that handles any page setup during a GE export
 */
(function (module) {
    module.factory('pr.genericExportsService', [
        '$http',
        '$routeParams',
        '$sanitize',
        '$translate',
        'rsEnvironment',
        'prActiveReport',
        'pr.API_ROUTES',
        'pr.loggerService',
        'pr.widgetManager',
        'prFlowingPages',
        'prPPTExportRegionsService',
        'prEventService',
        'pr.pageContextManager',
        'prOverrideTrigger',
        'pr.OVERRIDE_EVENTS',
        'pr.navigationService',
        'prPptxEditableWidgets',
        'prPageHeaderFooterExportLoadingService',
        function ($http, $routeParams, $sanitize, $translate, env, activeReport, API_ROUTES, loggerService, widgetManager, flowingPages, prPPTExportRegionsService, eventService, pageContextManager, triggerOverrides, OVERRIDE_EVENTS, navigationService, prPPTXEditableWidgets, pageHeaderFooterExportMap) {
            var logger = loggerService.getLoggerManager('pr.genericExportsService');
            var isPageReadyForExport = function () {
                var isMultiSectionExport = $routeParams.startSectionId || $routeParams.sectionId;
                return isMultiSectionExport ?
                    isMultiSectionExportEnvironmentReady() :
                    isSinglePageExportEnvironmentReady();
            };
            // Angularjs loads directives & components asyncronously. When there
            // are no widgets on a page sometimes the export will start before
            // the page numbers have been set.
            var isPageHeaderOrFooterDoneLoading = false;
            eventService.on('pageHeaderOrFooterIsRendered', function () {
                isPageHeaderOrFooterDoneLoading = true;
            });
            var setupExport = function (skipCheckWidgetReady) {
                // We want to delay the export from being ready for half a second just in case there
                // are some widget rendering or other issues that take some time
                var exportReady = false;
                /**
                 * Returns a boolean indicating whether the report is ready to
                 * be exported by GE (return value = true) or whether GE needs
                 * to wait for the report to render more (return value = false).
                 *
                 * If this is the final call, we need to directly send any queued
                 * logs because GE does not trigger the 'beforeunload'
                 * event when exiting the chrome headless browser.
                 *
                 * OP-5000: checkReady cannot be async. GE does not correctly handle errors when it is async.
                 */
                var checkExportReady = function (genericExportArgs) {
                    if (genericExportArgs === void 0) { genericExportArgs = {}; }
                    if (genericExportArgs.isFinalCall) {
                        // The export is incomplete and has timed out, this is the final call from GE.
                        // This only triggers when checkExportValidity parameter is passed to GE from prs
                        if (env.get(environment_1.ENV_VARS.FEATURES)[feature_flipper_1.FEATURES.FAIL_360_EXPORTS_ON_TIMEOUT] || reportContainsUnexpectedErrors()) {
                            var options = triggerOverrides({
                                event: OVERRIDE_EVENTS.FAIL_EXPORT_ON_TIMEOUT,
                                sync: true,
                                throwErrors: true
                            });
                            if (options === null || options === void 0 ? void 0 : options.shouldFailExport) {
                                logger.error({
                                    message: 'EXPORT_TIMEOUT: failing export',
                                    widgetStatuses: widgetManager.getWidgetStatusesLog()
                                });
                                // We are not awaiting here b/c GE does not support checkReady being async
                                logger.sendBatch();
                                return false;
                            }
                        }
                        // We must return `true` so that GE still generates the export.
                        // Otherwise, GE will not generate the export. This causes emails to not send!
                        logger.error({
                            message: 'EXPORT_TIMEOUT: exporting an incomplete report',
                            widgetStatuses: widgetManager.getWidgetStatusesLog()
                        });
                        // We are not awaiting here b/c GE does not support checkReady being async
                        logger.sendBatch();
                        return true;
                    }
                    if (skipCheckWidgetReady) {
                        // if there is a lang query param, wait for the translation to finish updating
                        if ($routeParams.lang && $translate.use() !== $sanitize(lodash_1.default.toUpper($routeParams.lang))) {
                            return false;
                        }
                        return true;
                    }
                    if (!navigationService.isSingleWidget && !isPageHeaderOrFooterDoneLoading) {
                        return false;
                    }
                    if (!pageHeaderFooterExportMap.isDoneLoading()) {
                        return false;
                    }
                    if (widgetManager.isReportReady(genericExportArgs)) {
                        logger.sendBatch();
                        setTimeout(function () {
                            exportReady = true;
                        }, 500);
                    }
                    return exportReady;
                };
                // Used by GE to deterimine whether a GE export section has widget
                // content. If this returns false, there is no widget content and GE will
                // not include a snapshot of this section in the final pdf to prevent blank pages.
                // This will be called after checkReady returns true.
                var hasContent = function () {
                    var containerPages = activeReport.getPages();
                    var startContainerPage = lodash_1.default.findIndex(containerPages, function (page) { return page._id === $routeParams.startSectionId; });
                    // This should never happen
                    if (startContainerPage === -1) {
                        return true;
                    }
                    // If it is the last section and no previous export section has content
                    // return true so that GE still prints out a blank page
                    var numOfSections = parseInt($routeParams.numOfSections, 10);
                    var isLastExportSection = (startContainerPage + numOfSections) >= containerPages.length;
                    var anySectionHasContent = pageContextManager.hasExportHadContent();
                    if (isLastExportSection && !anySectionHasContent) {
                        return true;
                    }
                    var activeWidgets = widgetManager.getActiveWidgets();
                    return activeWidgets.length > 0;
                };
                // Called by GE multi-page export opening the page
                // pageContext would be passed to next page
                var setPageContext = function (pageContext) {
                    if (!pageContext) {
                        pageContext = {
                            startPageNum: 0,
                            anySectionHasContent: false
                        };
                    }
                    pageContextManager.setPageContext(pageContext);
                    return pageContext;
                };
                // Called by GE multi-page export when taking the screenshot
                // pageContext would be passed to next page
                var getPageContext = function () {
                    var sectionHasContent = hasContent();
                    var currentPageNumber = pageContextManager.getStartPageNumber();
                    var startPageNum = currentPageNumber + flowingPages.getPages().length;
                    if (env.get(environment_1.ENV_VARS.FEATURES)[feature_flipper_1.FEATURES.CHECK_FOR_CONTENT]) {
                        startPageNum = sectionHasContent ? startPageNum : currentPageNumber;
                    }
                    return {
                        startPageNum: startPageNum,
                        anySectionHasContent: pageContextManager.hasExportHadContent() || sectionHasContent
                    };
                };
                var tryParseJSON = function (jsonString) {
                    try {
                        return JSON.parse(jsonString);
                    }
                    catch (e) {
                        console.warn('Failed to parse JSON string in prepareForExport: ' + jsonString);
                        return {};
                    }
                };
                // Called by GE once the page loads
                var prepareForExport = function (prepareForExportArguments) {
                    var filters = tryParseJSON(lodash_1.default.get(prepareForExportArguments, 'environment.filters', {}));
                    var privateExportContextMeta = tryParseJSON(lodash_1.default.get(prepareForExportArguments, 'environment.privateExportContextMeta', {}));
                    env.set(environment_1.ENV_VARS.FILTERS, filters);
                    env.set(environment_1.ENV_VARS.PRIVATE_EXPORT_META, privateExportContextMeta);
                    eventService.trigger('privateExportEnvSet');
                };
                // More info on GE options: https://gitlab-app.eng.qops.net/visualization-platform/generic-exports#options
                window.genericExports = {
                    getExportRegion: widgetManager.getSingleWidgetRect,
                    getExportRegions: prPPTExportRegionsService.getRegions,
                    checkReady: checkExportReady,
                    getEditableWidgets: getEditableWidgets,
                    getPageContext: getPageContext,
                    prepareForExport: prepareForExport,
                    setPageContext: setPageContext,
                    hasContent: hasContent
                };
                // return the genericExports object for testing
                return window.genericExports;
            };
            var getEditableWidgets = function () {
                return prPPTXEditableWidgets.getEditableWidgets();
            };
            var isMultiSectionExportEnvironmentReady = function () {
                // pageContext must be set by windows.genericExport.setPageContext
                return isPrivateExportEnvironmentReady() && pageContextManager.isPageContextSet();
            };
            var isSinglePageExportEnvironmentReady = function () {
                return isPrivateExportEnvironmentReady();
            };
            var isPrivateExportEnvironmentReady = function () {
                if (env.get(environment_1.ENV_VARS.IS_PRIVATE_EXPORT)) {
                    return !!(env.get(environment_1.ENV_VARS.FILTERS) && env.get(environment_1.ENV_VARS.PRIVATE_EXPORT_META));
                }
                return true;
            };
            var reportContainsUnexpectedErrors = function () {
                return !lodash_1.default.isEmpty(widgetManager.getErroredWidgets());
            };
            return {
                isPageReadyForExport: isPageReadyForExport,
                setupExport: setupExport
            };
        }
    ]);
})(angular_1.module('qualtrics.pr'));
